import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { House } from '@/client';
import { acceptSharingInvitationHousesHouseIdSharingAcceptPostMutation } from '@/client/@tanstack/react-query.gen';
import { Roles } from '@/utils/roleUtils';

/**
 * Silently accepts a sharing invitation when a non-owner user first visits a property.
 *
 * Owners get their association via house creation, not sharing — their
 * `invite_accepted_at` is naturally null and should stay that way.
 * For everyone else (admin/contributor/viewer), calling `/accept` stamps
 * `invite_accepted_at = now()`, moving their status from "invited" → "registered"
 * in the sharing list visible to the property owner.
 *
 * The endpoint is idempotent — safe to call multiple times.
 */
export const useAcceptInvitation = (house: House | null | undefined) => {
	const acceptedRef = useRef<Set<number>>(new Set());

	const { mutate } = useMutation({
		...acceptSharingInvitationHousesHouseIdSharingAcceptPostMutation(),
	});

	useEffect(() => {
		if (!house) return;

		const userRole = house.user_role;
		if (!userRole) return;

		const isOwner = userRole.role === Roles.Owner;
		const alreadyAccepted = userRole.invite_accepted_at != null;
		const alreadyCalled = acceptedRef.current.has(house.id);

		if (isOwner || alreadyAccepted || alreadyCalled) return;

		acceptedRef.current.add(house.id);
		mutate({ path: { house_id: house.id } });
	}, [house, mutate]);
};
